/**
 * When two charts are the same chart.
 *
 * Used to decide whether the card in front of you is already on the dashboard.
 * The answer has to be the whole presentation, not just the question: a bar
 * chart and a pie chart of the same question are two different charts and both
 * are worth pinning, while a second bar chart identical to the first is not.
 *
 * So the identity is the spec itself — everything that would be stored. If
 * pinning would produce a spec you already have, you already have it.
 */
/**
 * A stable string for any JSON value.
 *
 * Keys are sorted because a spec built in the browser and the same spec read
 * back from Mongo need not agree on property order, and two identical charts
 * that stringify differently would both be pinnable.
 *
 * Arrays keep their order: `rows` and `columns` are positional, and so are
 * `filters`.
 */
const canonical = (value) => {
    if (value === null || typeof value !== 'object')
        return JSON.stringify(value) ?? 'null';
    if (Array.isArray(value))
        return `[${value.map(canonical).join(',')}]`;
    const entries = Object.entries(value)
        // `undefined` and a missing key mean the same thing to a stored spec, so
        // they must not produce different identities.
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries
        .map(([key, v]) => `${JSON.stringify(key)}:${canonical(v)}`)
        .join(',')}}`;
};
/** Two specs that produce the same string are the same chart. */
export const specIdentity = (spec) => spec ? canonical(spec) : '';
/**
 * The question a spec charts, for counting how many views of it are pinned.
 *
 * Coarser than `specIdentity` on purpose: it answers "have I pinned this
 * question at all", which is what stops an empty pin icon looking like a bug
 * when you know perfectly well you pinned that question as something else.
 */
export const questionIdentity = (field) => {
    const ref = field;
    if (!ref)
        return null;
    return String(ref.questionId ?? ref.questionKey ?? ref.label ?? '') || null;
};
